<template>
  <section class="section">
    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; flex-wrap: wrap; gap: 12px;">
      <div>
        <h1 class="title is-4" style="margin-bottom: 2px;">Guías de Envío</h1>
        <p class="has-text-grey is-size-6">{{ total }} guías</p>
      </div>
      <div class="control has-icons-left">
        <input
          class="input"
          type="text"
          placeholder="Buscar por producto, ID o número de guía..."
          v-model="searchInput"
          @input="onSearch"
          style="min-width: 320px;"
        >
        <span class="icon is-left">
          <i class="material-icons" style="font-size: 18px;">search</i>
        </span>
      </div>
    </div>

    <div class="box" style="padding: 0; overflow: hidden;">
      <div v-if="isLoading" class="has-text-centered" style="padding: 60px;">
        <span class="icon is-large has-text-grey-light">
          <i class="material-icons" style="font-size: 48px;">local_shipping</i>
        </span>
        <p class="has-text-grey mt-2">Cargando guías...</p>
      </div>

      <div v-else-if="guides.length === 0" class="has-text-centered" style="padding: 60px;">
        <span class="icon is-large has-text-grey-light">
          <i class="material-icons" style="font-size: 48px;">local_shipping</i>
        </span>
        <p class="has-text-grey mt-2">{{ searchInput ? 'Sin resultados para la búsqueda' : 'No hay guías aún' }}</p>
      </div>

      <table v-else class="table is-fullwidth is-hoverable is-striped" style="margin: 0;">
        <thead>
          <tr style="background: #f8f9fa;">
            <th class="th-cell">ID</th>
            <th class="th-cell">Producto</th>
            <th class="th-cell">Usuario</th>
            <th class="th-cell">Estado</th>
            <th class="th-cell">Número de Guía</th>
            <th class="th-cell">Solicitada</th>
            <th class="th-cell">Generada</th>
            <th class="th-cell">Enviada</th>
            <th class="th-cell">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="guide in guides" :key="guide.deliveryId">
            <td class="td-cell">
              <span class="has-text-grey" style="font-size: 13px;">#{{ guide.productId }}</span>
            </td>
            <td class="td-cell">
              <router-link
                :to="`/productos/detalle/${guide.productId}`"
                class="has-text-dark"
                style="font-weight: 500; font-size: 14px;"
              >
                {{ guide.productName }}
              </router-link>
            </td>
            <td class="td-cell">
              <router-link
                :to="`/usuarios/detalle/${guide.userId}`"
                class="has-text-grey"
                style="font-size: 13px;"
              >
                {{ guide.username || guide.userName }}
              </router-link>
            </td>
            <td class="td-cell">
              <span
                class="tag"
                :class="statusTag(guide).clazz"
                :title="guide.hasError ? (guide.errorMessage || 'Error al generar') : ''"
                style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;"
              >
                {{ statusTag(guide).label }}
              </span>
            </td>
            <td class="td-cell">
              <span v-if="guide.trackingNumber" class="tag is-light is-info" style="font-family: monospace; font-size: 12px;">
                {{ guide.trackingNumber }}
              </span>
              <span v-else class="has-text-grey" style="font-size: 13px;">—</span>
            </td>
            <td class="td-cell">
              <span class="has-text-grey" style="font-size: 13px;">{{ formatDate(guide.requestedAt) }}</span>
            </td>
            <td class="td-cell">
              <span class="has-text-grey" style="font-size: 13px;">{{ formatDate(guide.qrGeneratedAt) }}</span>
            </td>
            <td class="td-cell">
              <span class="has-text-grey" style="font-size: 13px;">{{ guide.shippedAt ? formatDate(guide.shippedAt) : '—' }}</span>
            </td>
            <td class="td-cell">
              <div class="buttons are-small">
                <button
                  v-if="canGenerate(guide)"
                  class="button is-link"
                  :class="{ 'is-loading': generatingId === guide.deliveryId }"
                  :disabled="generatingId !== null"
                  @click="onGenerate(guide)"
                  :title="guide.hasError ? 'Reintentar generación' : 'Aprobar y generar guía'"
                >
                  <span class="icon"><i class="material-icons" style="font-size: 16px;">{{ guide.hasError ? 'refresh' : 'check' }}</i></span>
                  <span>{{ guide.hasError ? 'Reintentar' : 'Generar' }}</span>
                </button>
                <a
                  v-if="guide.labelUrl"
                  :href="guide.labelUrl"
                  target="_blank"
                  class="button is-link is-light"
                  title="Descargar PDF de guía"
                >
                  <span class="icon"><i class="material-icons" style="font-size: 16px;">download</i></span>
                  <span>PDF</span>
                </a>
                <router-link
                  :to="`/productos/detalle/${guide.productId}`"
                  class="button is-light"
                  title="Ver producto"
                >
                  <span class="icon"><i class="material-icons" style="font-size: 16px;">open_in_new</i></span>
                </router-link>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <base-pagination
      class="is-medium"
      :page="currentPage"
      :pages="parseInt(pages)"
      :action="paginator"
      :loading="isLoading"
    />

    <!-- Modal: confirmar generación de guía -->
    <div class="modal" :class="{ 'is-active': showModal }">
      <div class="modal-background" @click="closeModal"></div>
      <div class="modal-card" style="max-width: 480px;">
        <header class="modal-card-head">
          <p class="modal-card-title is-size-5">
            {{ modalGuide && modalGuide.hasError ? 'Reintentar guía de envío' : 'Generar guía de envío' }}
          </p>
          <button class="delete" aria-label="close" @click="closeModal" :disabled="generating"></button>
        </header>
        <section class="modal-card-body">
          <p v-if="modalGuide" class="has-text-grey mb-4" style="font-size: 14px;">
            Producto: <strong class="has-text-dark">{{ modalGuide.productName }}</strong>
          </p>

          <!-- Cotización del costo -->
          <div class="box" style="box-shadow: none; border: 1px solid #ededed;">
            <div v-if="quoteLoading" class="has-text-centered" style="padding: 8px;">
              <span class="icon is-medium has-text-grey-light">
                <i class="material-icons spin" style="font-size: 28px;">autorenew</i>
              </span>
              <p class="has-text-grey mt-2" style="font-size: 13px;">Calculando costo de la guía...</p>
            </div>
            <div v-else-if="quote">
              <div style="display: flex; align-items: baseline; justify-content: space-between;">
                <span class="has-text-grey" style="font-size: 14px;">Costo de la guía</span>
                <span class="has-text-dark" style="font-size: 24px; font-weight: 700;">
                  {{ formatMoney(quote.cost, quote.currency) }}
                </span>
              </div>
              <p v-if="quote.carrier || quote.deliveryType" class="has-text-grey mt-1" style="font-size: 12px;">
                {{ [quote.carrier, quote.deliveryType].filter(Boolean).join(' · ') }}
              </p>
              <p class="has-text-grey mt-2" style="font-size: 12px;">
                Este monto se descontará del saldo de la cuenta de EnvíoClick.
              </p>
            </div>
          </div>

          <!-- Error -->
          <div v-if="modalError" class="notification is-danger is-light" style="font-size: 13px;">
            <strong>No se pudo continuar:</strong><br>
            {{ modalError }}
          </div>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end;">
          <button class="button" @click="closeModal" :disabled="generating">Cancelar</button>
          <button
            class="button is-link"
            :class="{ 'is-loading': generating }"
            :disabled="generating || quoteLoading || !quote"
            @click="confirmGenerate"
          >
            <span class="icon"><i class="material-icons" style="font-size: 16px;">check</i></span>
            <span>{{ modalGuide && modalGuide.hasError ? 'Reintentar' : 'Generar guía' }}</span>
          </button>
        </footer>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'Guias',
  components: {
    BasePagination: () => import('@/components/BasePagination')
  },
  data () {
    return {
      guides: [],
      total: 0,
      pages: 1,
      currentPage: 1,
      searchInput: '',
      isLoading: false,
      searchTimeout: null,
      generatingId: null,
      // Modal de generación de guía
      showModal: false,
      modalGuide: null,
      quote: null,
      quoteLoading: false,
      modalError: '',
      generating: false
    }
  },
  methods: {
    ...mapActions(['getDhlGuides', 'generateDhlGuide', 'getDhlGuideQuote']),

    async fetchGuides () {
      this.isLoading = true
      const result = await this.getDhlGuides({
        page: this.currentPage,
        search: this.searchInput,
        limit: 20
      })
      if (result) {
        this.guides = result.payload || []
        this.total = result.total || 0
        this.pages = result.pages || 1
      }
      this.isLoading = false
    },

    onSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchGuides()
      }, 400)
    },

    paginator (newPage) {
      if (newPage < 1 || newPage > this.pages) return
      this.currentPage = newPage
      this.fetchGuides()
    },

    canGenerate (guide) {
      return !guide.trackingNumber && (guide.requestedAt || guide.hasError)
    },

    async onGenerate (guide) {
      // Abrir modal y cotizar el costo antes de generar
      this.modalGuide = guide
      this.quote = null
      this.modalError = ''
      this.generating = false
      this.showModal = true

      this.quoteLoading = true
      const result = await this.getDhlGuideQuote({ deliveryId: guide.deliveryId })
      this.quoteLoading = false

      if (result.success) {
        this.quote = result.payload
      } else {
        this.modalError = result.error || 'No se pudo calcular el costo de la guía.'
      }
    },

    closeModal () {
      if (this.generating) return
      this.showModal = false
      this.modalGuide = null
      this.quote = null
      this.modalError = ''
      this.quoteLoading = false
    },

    async confirmGenerate () {
      if (!this.modalGuide) return
      this.modalError = ''
      this.generating = true
      this.generatingId = this.modalGuide.deliveryId

      const result = await this.generateDhlGuide({ deliveryId: this.modalGuide.deliveryId })

      this.generating = false
      this.generatingId = null

      if (!result.success) {
        this.modalError = result.error || 'No se pudo generar la guía.'
        return
      }

      this.showModal = false
      this.modalGuide = null
      this.quote = null
      await this.fetchGuides()
    },

    statusTag (guide) {
      if (guide.hasError) return { clazz: 'is-danger is-light', label: 'Con error' }
      if (guide.trackingNumber) return { clazz: 'is-success is-light', label: 'Generada' }
      if (guide.requestedAt) return { clazz: 'is-warning is-light', label: 'Pendiente' }
      return { clazz: 'is-light', label: 'Sin solicitud' }
    },

    formatDate (date) {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatMoney (amount, currency = 'MXN') {
      if (amount === null || amount === undefined || isNaN(amount)) return '—'
      try {
        return new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: currency || 'MXN'
        }).format(amount)
      } catch (e) {
        return `$${Number(amount).toFixed(2)} ${currency || ''}`.trim()
      }
    }
  },

  mounted () {
    this.fetchGuides()
  }
}
</script>

<style scoped>
.th-cell {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 14px 16px;
  color: #4a4a4a;
}
.td-cell {
  padding: 12px 16px;
  vertical-align: middle;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
